// Wave 20.49cx — AERO AI Route Planner (multi-option, web).
//
// Rider opens from the weather panel. This fullscreen dialog
// seeds the picker with the rider's typical outdoor distance
// (median of last 5 sessions), fetches multiple candidate loops
// from /route/plan-aero, renders each as a coloured polyline
// (green = least headwind, red = most), and shows the selected
// one's stats in the footer. Tapping a chip focuses that route.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import LayersIcon from '@mui/icons-material/Layers';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { colors } from '../theme';
import { useMapStyle, mapStyleUrl, MAP_STYLE_LABELS, type MapStyle } from '../services/useMapStyle';
import { attachMinimalAttribution } from '../services/mapAttribution';
import { useUnitSystem, fmtDistance, fmtSpeed } from '../services/useUnitSystem';
import WindParticles from './WindParticles';
import { buildGpx, downloadGpx } from '../services/gpx';
import { cumulativeM, sliceByDistance } from '../services/routeGeometry';
import { cueText, type Cue } from '../services/aeroCues';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';
// Wave 20.49di — Same proxy path everyone else uses now.
const WEATHER_API = `${API}/weather`;
// Wave 20.49cv — Wind on/off preference (same key on mobile).
const WIND_PREF_KEY = 'aero.chat_planner_wind';

// Wave 20.49de — Same pk fallback other maps use (RouteMap /
// WeatherPanel / Feed). The production CRA build doesn't set
// REACT_APP_MAPBOX_ACCESS_TOKEN, so without a fallback the map
// silently stays blank. Set at module scope on the mapboxgl
// namespace instead of passing per-Map init — matches the
// working pattern in the codebase.
const MAPBOX_TOKEN =
  process.env.REACT_APP_MAPBOX_TOKEN ||
  process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ||
  'pk.eyJ1IjoiYWVyby1jaGF0IiwiYSI6ImNtY3J4bnp0MTBjeTYybm42bzVjbWtsdmkifQ.rbgswhST93lMRY-CzQZ05Q';
mapboxgl.accessToken = MAPBOX_TOKEN;

interface Props {
  open: boolean;
  onClose: () => void;
  coords: { lat: number; lng: number };
  timestamp: string;
}

interface RouteOption {
  id: string;
  rank: number;
  distanceKm: number;
  durationSec: number;
  bearing: number;
  direction: 'cw' | 'ccw';
  headwindScore: number;
  intensity: number;
  // Wave 20.49cu — optional: older API builds don't send them.
  loopQuality?: 'loop' | 'loop_from_home' | 'partial_out_and_back' | 'mostly_out_and_back';
  retracedKm?: number;
  geometry: {
    type: 'LineString';
    coordinates: [number, number][];
  };
}

// Wave 20.50 — the aero brief for the selected loop. Optional
// everywhere: an older API build simply returns no brief and the panel
// stays hidden.
interface TuckZone {
  startM: number;
  endM: number;
  lengthM: number;
  headwindMps: number;
  savingsSec: number;
}

interface AeroBrief {
  windAvailable: boolean;
  position: {
    defaultClass: string;
    defaultCda: number;
    defaultTested: boolean;
    tuckClass: string;
    tuckCda: number;
    tuckTested: boolean;
    derivedFrom: string | null;
  };
  speedMps: number;
  totalDistanceM: number;
  predictedDurationSec: number;
  totalSavingsSec: number;
  tuckZones: TuckZone[];
  cues: Cue[];
}

interface PlanResponse {
  forecast: {
    wind_speed_ms: number | null;
    wind_from_deg: number | null;
    t: string;
  };
  options: RouteOption[];
  candidatesConsidered: number;
  candidatesReturned: number;
}

// Wave 20.49dc — Include short-ride chips (2/3 mi, 3/5 km)
// because riders with test-only histories or actually-short
// commutes were seeing a 5-mi minimum route length longer than
// anything they'd ever ridden.
const DISTANCE_CHOICES_KM = [3, 5, 10, 15, 20, 30, 50, 80];
const DISTANCE_CHOICES_MI = [2, 3, 5, 10, 15, 20, 30, 50];
const MI_TO_KM = 1.609344;

const RAMP = ['#6aff45', '#c9ff45', '#ffd166', '#ff8a45', '#ff5a5f'];
function colourForIntensity(t: number): string {
  const clamped = Math.max(0, Math.min(1, t));
  const scaled = clamped * (RAMP.length - 1);
  const lo = Math.floor(scaled);
  const hi = Math.min(RAMP.length - 1, lo + 1);
  const f = scaled - lo;
  return blendHex(RAMP[lo], RAMP[hi], f);
}
function blendHex(a: string, b: string, f: number): string {
  const pa = hexToRgb(a);
  const pb = hexToRgb(b);
  const r = Math.round(pa[0] + (pb[0] - pa[0]) * f);
  const g = Math.round(pa[1] + (pb[1] - pa[1]) * f);
  const bl = Math.round(pa[2] + (pb[2] - pa[2]) * f);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${bl.toString(16).padStart(2, '0')}`;
}
function hexToRgb(h: string): [number, number, number] {
  const n = parseInt(h.slice(1), 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}
function bearingLabel(bearing: number, direction: 'cw' | 'ccw'): string {
  const compass = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const b = compass[Math.round(bearing / 45) % 8];
  return `${b} · ${direction === 'cw' ? 'CW' : 'CCW'}`;
}
function formatDuration(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}
// Wave 20.50 — savings are small numbers; "1:12" reads better than
// "1m" and "48s" better than "0m".
function formatSavings(sec: number): string {
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
function compass(deg: number | null | undefined): string {
  if (deg == null) return '';
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return dirs[Math.round(((deg % 360) / 45)) % 8];
}

export default function AeroRoutePlanner({ open, onClose, coords, timestamp }: Props) {
  // Wave 20.49cw — Planner-local basemap. Seeded from the global
  // setting; changing it here does NOT touch the global setting.
  const globalMapStyle = useMapStyle();
  const [mapStyle, setLocalMapStyle] = useState<MapStyle>(globalMapStyle);
  const mapStyleRef = useRef<MapStyle>(globalMapStyle);
  mapStyleRef.current = mapStyle;
  const appliedStyleRef = useRef<MapStyle>(globalMapStyle);
  // Bumped on every `style.load` so the routes effect re-adds its
  // sources/layers after a basemap swap wipes them.
  const [styleVersion, setStyleVersion] = useState(0);
  const cycleMapStyle = () => {
    const order = Object.keys(MAP_STYLE_LABELS) as MapStyle[];
    setLocalMapStyle((cur) => order[(order.indexOf(cur) + 1) % order.length]);
  };
  const units = useUnitSystem();
  const isImperial = units === 'imperial';

  const [data, setData] = useState<PlanResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [distanceInUnit, setDistanceInUnit] = useState<number>(isImperial ? 15 : 25);
  const [seededDefault, setSeededDefault] = useState(false);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [mapReady, setMapReady] = useState<mapboxgl.Map | null>(null);
  const [windFrames, setWindFrames] = useState<Array<{ url: string; bbox: [number, number, number, number] }>>([]);
  // Wave 20.60 — Plan from somewhere other than here. Clicking the map
  // moves the start; the button puts it back. Cleared when the dialog
  // closes, so it always opens planning from the rider's real
  // location.
  const [originOverride, setOriginOverride] = useState<{ lat: number; lng: number } | null>(null);
  const origin = originOverride ?? coords;
  // Rounded so a metre of GPS jitter can't retrigger a whole replan.
  const originKey = `${origin.lat.toFixed(4)},${origin.lng.toFixed(4)}`;
  useEffect(() => {
    if (!open) setOriginOverride(null);
  }, [open]);
  // Wave 20.50 — aero brief for whichever option is selected.
  const [brief, setBrief] = useState<AeroBrief | null>(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);
  const [rideState, setRideState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');
  // Wave 20.49cv — Wind on/off so the rider can read the routes
  // with and without the particle field. Persisted; default on.
  const [windOn, setWindOn] = useState<boolean>(() => {
    try {
      return localStorage.getItem(WIND_PREF_KEY) !== 'off';
    } catch {
      return true;
    }
  });
  const toggleWind = () =>
    setWindOn((v) => {
      const next = !v;
      try {
        localStorage.setItem(WIND_PREF_KEY, next ? 'on' : 'off');
      } catch {
        /* private mode — in-memory only */
      }
      return next;
    });
  // Wave 20.49dj — Track the container by a mutable ref for
  // effects that need to peek, but do the ACTUAL init from a
  // callback ref that fires the moment React attaches the DOM
  // node. This is the crucial change: MUI's fullscreen Dialog
  // renders children through a Portal + Fade transition and
  // the containing Box mounts AFTER `open` flips true. A
  // useEffect gated on the imperative ref value was firing
  // once, seeing containerRef.current === null, and never
  // running again — that's why the map has been a black
  // rectangle in production for the last few pushes.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const timersRef = useRef<number[]>([]);
  const followupObsRef = useRef<ResizeObserver | null>(null);
  const initMap = useCallback((el: HTMLDivElement) => {
    if (mapRef.current) return;
    const m = new mapboxgl.Map({
      container: el,
      style: mapStyleUrl(mapStyleRef.current),
      center: [coords.lng, coords.lat],
      zoom: 10,
      attributionControl: false,
      logoPosition: 'bottom-right',
    });
    appliedStyleRef.current = mapStyleRef.current;
    attachMinimalAttribution(m);
    m.on('style.load', () => setStyleVersion((v) => v + 1));
    mapRef.current = m;
    for (const delay of [0, 16, 60, 150, 300, 600, 1200]) {
      timersRef.current.push(
        window.setTimeout(() => {
          try { m.resize(); } catch { /* noop */ }
        }, delay),
      );
    }
    if (typeof ResizeObserver !== 'undefined') {
      const obs = new ResizeObserver(() => {
        try { m.resize(); } catch { /* noop */ }
      });
      obs.observe(el);
      followupObsRef.current = obs;
    }
    m.on('error', (e) => {
      // eslint-disable-next-line no-console
      console.warn('[AeroRoutePlanner] mapbox error', e?.error);
    });
    // Wave 20.60 — click to plan from there. mapbox-gl only fires
    // 'click' on a discrete click, so this doesn't fight drag or zoom.
    m.on('click', (ev) => {
      const { lng, lat } = ev.lngLat;
      if (!isFinite(lng) || !isFinite(lat)) return;
      setOriginOverride({ lng, lat });
    });
    m.once('load', () => setMapReady(m));
  }, [coords.lat, coords.lng]);

  // Wave 20.49cw — Swap the basemap in place when the local style
  // changes. Sources/layers are re-added by the routes effect on
  // the resulting `style.load` (styleVersion).
  useEffect(() => {
    if (!mapReady || appliedStyleRef.current === mapStyle) return;
    appliedStyleRef.current = mapStyle;
    try {
      mapReady.setStyle(mapStyleUrl(mapStyle));
    } catch {
      /* map disposed mid-swap */
    }
  }, [mapReady, mapStyle]);
  const disposeMap = useCallback(() => {
    for (const id of timersRef.current) window.clearTimeout(id);
    timersRef.current = [];
    followupObsRef.current?.disconnect();
    followupObsRef.current = null;
    if (mapRef.current) {
      try { mapRef.current.remove(); } catch { /* noop */ }
      mapRef.current = null;
    }
    setMapReady(null);
  }, []);
  const containerCallbackRef = useCallback((el: HTMLDivElement | null) => {
    containerRef.current = el;
    if (el) initMap(el);
    else disposeMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initMap, disposeMap]);

  const distanceKm = isImperial ? distanceInUnit * MI_TO_KM : distanceInUnit;
  const distanceChoices = isImperial ? DISTANCE_CHOICES_MI : DISTANCE_CHOICES_KM;
  const distanceUnitLabel = isImperial ? 'mi' : 'km';

  useEffect(() => {
    if (!open || seededDefault) return;
    const jwt = localStorage.getItem(TOKEN_KEY);
    if (!jwt) return;
    fetch(`${API}/route/typical-distance`, { headers: { Authorization: `Bearer ${jwt}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!j) return;
        const km = j.distanceKm ?? 30;
        const inUnit = isImperial ? Math.round(km / MI_TO_KM) : Math.round(km);
        const snapped = distanceChoices.reduce(
          (best, c) => (Math.abs(c - inUnit) < Math.abs(best - inUnit) ? c : best),
          distanceChoices[0],
        );
        setDistanceInUnit(snapped);
      })
      .finally(() => setSeededDefault(true));
  }, [open, seededDefault, isImperial, distanceChoices]);

  const fetchPlan = async () => {
    setLoading(true);
    setError(null);
    setData(null);
    setSelectedId(null);
    try {
      const jwt = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(
        `${API}/route/plan-aero?originLat=${origin.lat}&originLng=${origin.lng}&distanceKm=${distanceKm.toFixed(3)}&t=${encodeURIComponent(timestamp)}`,
        { headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined },
      );
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.message ?? 'Route planner unavailable — try again in a minute.');
      }
      const j: PlanResponse = await res.json();
      setData(j);
      if (j.options.length > 0) setSelectedId(j.options[0].id);
    } catch (e: any) {
      setError(e?.message ?? 'Route planner unavailable — try again in a minute.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && seededDefault) void fetchPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seededDefault, distanceInUnit, timestamp, originKey]);

  // Init/dispose now happens from the callback ref above so we
  // don't miss late DOM attachment.

  // Wave 20.49di — Wind particle overlay: same WindParticles
  // component the WeatherPanel uses on the Feed map. Rebuild
  // the wind PNG URL when the map viewport moves, when a new
  // route set arrives, and on the hourly refresh so the
  // animation always reflects the currently-selected forecast
  // hour.
  //
  // Wave 20.49dk — Extra dep on `data`: after the plan comes
  // back the routes effect calls fitBounds which zooms the map
  // OUT to encompass all candidate loops. The wind bbox
  // captured at map-load time was for the initial zoom-10
  // view (a small square around the origin), so particles
  // only advected within that small square — visible as a
  // rectangle much smaller than the map. Re-running build()
  // when `data` arrives, PLUS delaying a beat so fitBounds has
  // definitely settled, covers the race deterministically.
  useEffect(() => {
    if (!mapReady) return;
    const build = () => {
      const b = mapReady.getBounds();
      // Wave 20.49cv — Fetch the field for 1.5× the viewport so a
      // pan or zoom-out between `moveend`s still samples inside
      // the field. Particles that sampled outside the old bbox
      // were dying instantly, which is why the animation thinned
      // to nothing the moment the rider moved the map.
      const cx = (b.getWest() + b.getEast()) / 2;
      const cy = (b.getSouth() + b.getNorth()) / 2;
      const halfW = ((b.getEast() - b.getWest()) / 2) * 1.5;
      const halfH = ((b.getNorth() - b.getSouth()) / 2) * 1.5;
      let w = cx - halfW;
      let e = cx + halfW;
      let s = cy - halfH;
      let n = cy + halfH;
      if (e - w >= 360) {
        w = -180;
        e = 180;
      } else {
        w = Math.max(-180, Math.min(180, w));
        e = Math.max(-180, Math.min(180, e));
      }
      s = Math.max(-89.9, Math.min(89.9, s));
      n = Math.max(-89.9, Math.min(89.9, n));
      const bbox = `${w.toFixed(3)},${s.toFixed(3)},${e.toFixed(3)},${n.toFixed(3)}`;
      const spanDeg = Math.max(e - w, n - s);
      const size = spanDeg > 60 ? '1024x512' : spanDeg > 20 ? '768x384' : '384x192';
      const t = new Date(timestamp);
      const url = `${WEATHER_API}/wind/png?bbox=${encodeURIComponent(bbox)}&t=${encodeURIComponent(t.toISOString())}&size=${size}`;
      setWindFrames([{ url, bbox: [w, s, e, n] }]);
    };
    build();
    // Belt + suspenders: rebuild again after any settled camera
    // change (fitBounds fires moveend), and one more time after
    // 300 ms in case the settled bounds landed just before the
    // listener was attached.
    const onMove = () => build();
    mapReady.on('moveend', onMove);
    const settleTimer = window.setTimeout(build, 300);
    const id = window.setInterval(build, 10 * 60 * 1000);
    return () => {
      mapReady.off('moveend', onMove);
      window.clearTimeout(settleTimer);
      window.clearInterval(id);
    };
  }, [mapReady, timestamp, data]);

  // Wave 20.50 — Fetch the aero brief for the selected loop: where to
  // tuck, and what the wind does at each turn. Server-side because it
  // needs the rider's measured CdA per position.
  useEffect(() => {
    if (!open || !selectedId) {
      setBrief(null);
      return;
    }
    const opt = (data?.options ?? []).find((o) => o.id === selectedId);
    const coordinates = opt?.geometry?.coordinates;
    if (!Array.isArray(coordinates) || coordinates.length < 2) {
      setBrief(null);
      return;
    }
    let alive = true;
    setBriefLoading(true);
    setBrief(null);
    const jwt = localStorage.getItem(TOKEN_KEY);
    fetch(`${API}/route/aero-brief`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      },
      body: JSON.stringify({ coordinates, startT: timestamp }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: AeroBrief | null) => {
        if (alive && j && Array.isArray(j.tuckZones)) setBrief(j);
      })
      .catch((e) => {
        // Wave 20.52a — still calm for the rider, but no longer
        // invisible to us. A silently-failed brief is how a route got
        // saved with no aero stretches and no cues.
        // eslint-disable-next-line no-console
        console.warn('[planner] aero brief unavailable', e);
      })
      .finally(() => {
        if (alive) setBriefLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [open, selectedId, data, timestamp]);

  // Wave 20.50 — Paint the tuck zones on the map, under the route line
  // so the route still reads on top. This is the whole point of the
  // brief being spatial: "get low HERE", not "get low sometimes".
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const SRC = 'tuck-zones';
    const LYR = 'tuck-zones-line';
    const draw = () => {
      try {
        if (m.getLayer(LYR)) m.removeLayer(LYR);
        if (m.getSource(SRC)) m.removeSource(SRC);
      } catch {
        /* style swapped underneath us */
      }
      const opt = (data?.options ?? []).find((o) => o.id === selectedId);
      const coords = opt?.geometry?.coordinates as [number, number][] | undefined;
      if (!brief || !coords || coords.length < 2 || brief.tuckZones.length === 0) return;
      const cum = cumulativeM(coords);
      const features = brief.tuckZones
        .map((z) => sliceByDistance(coords, cum, z.startM, z.endM))
        .filter((line) => line.length >= 2)
        .map((line) => ({
          type: 'Feature' as const,
          properties: {},
          geometry: { type: 'LineString' as const, coordinates: line },
        }));
      if (!features.length) return;
      try {
        m.addSource(SRC, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features },
        });
        m.addLayer({
          id: LYR,
          type: 'line',
          source: SRC,
          paint: {
            'line-color': colors.data,
            'line-width': 14,
            'line-opacity': 0.42,
            'line-blur': 2,
          },
          layout: { 'line-cap': 'round', 'line-join': 'round' },
        });
      } catch {
        /* map disposed mid-draw */
      }
    };
    if (m.isStyleLoaded()) draw();
    else m.once('style.load', draw);
  }, [brief, selectedId, data, styleVersion]);

  // Wave 20.60 — marks where the rider clicked, straight away, before
  // the new routes come back. OSRM snaps the real start to the nearest
  // road; the route's own start pin marks that.
  const originMarkerRef = useRef<mapboxgl.Marker | null>(null);
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    if (!originOverride) {
      originMarkerRef.current?.remove();
      originMarkerRef.current = null;
      return;
    }
    if (!originMarkerRef.current) {
      const el = document.createElement('div');
      el.style.cssText =
        'width:18px;height:18px;border-radius:50%;box-sizing:border-box;' +
        `background:rgba(10,16,12,0.9);border:3px solid ${colors.brand};`;
      originMarkerRef.current = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat([originOverride.lng, originOverride.lat])
        .addTo(m);
    } else {
      originMarkerRef.current.setLngLat([originOverride.lng, originOverride.lat]);
    }
  }, [originOverride, styleVersion]);

  const startMarkerRef = useRef<mapboxgl.Marker | null>(null);
  // Only frame the routes when a NEW plan arrives — not on every
  // selection change or basemap swap, which would yank the camera
  // back from wherever the rider zoomed to.
  const lastFitDataRef = useRef<PlanResponse | null>(null);
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !data) return;
    const setup = () => {
      // Remove old sources / layers (both line + arrow layers).
      for (const layer of m.getStyle().layers ?? []) {
        if (
          layer.id.startsWith('route-line-') ||
          layer.id.startsWith('route-arrows-')
        ) {
          m.removeLayer(layer.id);
        }
      }
      for (const sourceId of Object.keys(m.getStyle().sources ?? {})) {
        if (sourceId.startsWith('route-')) m.removeSource(sourceId);
      }
      const b = new mapboxgl.LngLatBounds();
      for (const opt of data.options) {
        m.addSource(`route-${opt.id}`, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: opt.geometry as any,
          },
        });
        const isSel = opt.id === selectedId;
        m.addLayer({
          id: `route-line-${opt.id}`,
          type: 'line',
          source: `route-${opt.id}`,
          paint: {
            'line-color': colourForIntensity(opt.intensity),
            'line-width': isSel ? 5 : 3,
            'line-opacity': isSel ? 0.95 : 0.45,
          },
          layout: { 'line-cap': 'round', 'line-join': 'round' },
        });
        // Wave 20.49dj — Directional arrows along the selected
        // route. Only the selected route gets them so the map
        // doesn't clutter with all four options' arrows.
        if (isSel) {
          m.addLayer({
            id: `route-arrows-${opt.id}`,
            type: 'symbol',
            source: `route-${opt.id}`,
            layout: {
              'symbol-placement': 'line',
              'symbol-spacing': 120,
              'text-field': '▶',
              'text-size': 16,
              'text-keep-upright': false,
              'text-rotation-alignment': 'map',
              'text-pitch-alignment': 'map',
              'text-allow-overlap': true,
              'text-ignore-placement': true,
            },
            paint: {
              'text-color': '#0A100C',
              'text-halo-color': colourForIntensity(opt.intensity),
              'text-halo-width': 1.5,
            },
          });
        }
        for (const c of opt.geometry.coordinates) b.extend(c as any);
      }
      if (lastFitDataRef.current !== data) {
        lastFitDataRef.current = data;
        try {
          m.fitBounds(b, { padding: 60, maxZoom: 12, animate: false });
        } catch {
          /* single-point bounds fine */
        }
      }
      // Wave 20.49dj — Start pin at the first coordinate of
      // the selected route.
      const sel = data.options.find((o) => o.id === selectedId);
      const first = sel?.geometry?.coordinates?.[0];
      if (Array.isArray(first) && first.length >= 2) {
        if (!startMarkerRef.current) {
          const el = document.createElement('div');
          el.style.cssText =
            'width:20px;height:20px;border-radius:50%;' +
            `background:${colors.brand};border:3px solid #0A100C;` +
            'box-shadow:0 0 0 1px rgba(0,0,0,0.35);';
          startMarkerRef.current = new mapboxgl.Marker({
            element: el,
            anchor: 'center',
          })
            .setLngLat([first[0], first[1]])
            .addTo(m);
        } else {
          startMarkerRef.current.setLngLat([first[0], first[1]]);
        }
      } else if (startMarkerRef.current) {
        startMarkerRef.current.remove();
        startMarkerRef.current = null;
      }
    };
    if (m.isStyleLoaded()) setup();
    else m.once('style.load', setup);
    return () => {
      if (startMarkerRef.current) {
        startMarkerRef.current.remove();
        startMarkerRef.current = null;
      }
    };
  }, [data, selectedId, styleVersion]);

  const selected = useMemo(
    () => (data?.options ?? []).find((o) => o.id === selectedId) ?? null,
    [data, selectedId],
  );

  const distanceStr = selected ? fmtDistance(selected.distanceKm * 1000, units) : null;
  const durationStr = selected ? formatDuration(selected.durationSec) : null;
  // Wave 20.50 — "Ride this route": remember the loop server-side so
  // the phone can pick it up (the planner and the recorder are
  // different tabs, and usually different DEVICES), and hand the rider
  // a GPX for whatever head unit they already own.
  const routeName = selected
    ? `Aero ${fmtDistance(selected.distanceKm * 1000, units)} loop`
    : 'Aero route';

  const onRideThisRoute = async () => {
    if (!selected) return;
    setRideState('saving');
    try {
      const jwt = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}/route/planned`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
        body: JSON.stringify({
          name: routeName,
          coordinates: selected.geometry.coordinates,
          distanceM: Math.round(selected.distanceKm * 1000),
          startT: timestamp,
          // Freeze the brief with the route: the cues belong to this
          // hour's forecast, and a rider who saved the 7 AM plan should
          // hear the 7 AM plan.
          brief,
        }),
      });
      if (!res.ok) throw new Error(`save ${res.status}`);
      setRideState('saved');
    } catch (e) {
      // Wave 20.50a — the rider gets calm copy; we get the reason.
      // Previously this failed completely silently, which made a
      // working button and a broken one look identical.
      // eslint-disable-next-line no-console
      console.warn('[planner] could not save the planned route', e);
      setRideState('failed');
    }
  };

  const onDownloadGpx = () => {
    if (!selected) return;
    downloadGpx(
      routeName,
      buildGpx({
        name: routeName,
        coords: selected.geometry.coordinates,
        timeIso: timestamp,
        description: brief
          ? `Planned with aero.chat. ${brief.tuckZones.length} aero stretch${brief.tuckZones.length === 1 ? '' : 'es'}.`
          : 'Planned with aero.chat.',
      }),
    );
  };

  const savingsStr = brief ? formatSavings(brief.totalSavingsSec) : null;

  const windStr = data?.forecast?.wind_speed_ms != null
    ? `${fmtSpeed(data.forecast.wind_speed_ms, units)}${data.forecast.wind_from_deg != null ? ` ${compass(data.forecast.wind_from_deg)}` : ''}`
    : null;

  return (
    <Dialog
      open={open}
      fullScreen
      onClose={onClose}
      // Wave 20.49dc — On mobile-web (narrow viewport in a Chrome/
      // Safari tab), the default MUI Dialog Paper doesn't cascade a
      // column flex layout, so the map Box's `flex:1` didn't stretch
      // and the map container collapsed to 0 height — rider saw
      // "the map isn't showing at all". Force column flex on the
      // Paper so the middle Box actually gets height.
      PaperProps={{
        sx: {
          backgroundColor: colors.bg,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ p: 1.5, borderBottom: `1px solid ${colors.border}` }}>
        <IconButton onClick={onClose} sx={{ color: colors.text }}>
          <CloseIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ color: colors.brand, fontSize: '0.65rem', letterSpacing: '0.2em', fontWeight: 800 }}>
            AERO AI
          </Typography>
          <Typography sx={{ color: colors.text, fontSize: '1.05rem', fontWeight: 800, lineHeight: 1.1 }}>
            Route Planner
          </Typography>
        </Box>
        <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem' }}>
          {new Date(timestamp).toLocaleString(undefined, { weekday: 'short', hour: 'numeric' })}
        </Typography>
      </Stack>

      <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
        <Box ref={containerCallbackRef} sx={{ position: 'absolute', inset: 0 }} />
        {windOn && mapReady && windFrames.length > 0 && (
          <WindParticles key="planner-wind" map={mapReady as any} frames={windFrames} loopWallSec={20} />
        )}
        {/* Wave 20.60 — back to planning from where I actually am.
            Always present so it is discoverable, lit when a custom
            start is in play so the way back is obvious. */}
        <Box
          onClick={() => setOriginOverride(null)}
          role="button"
          aria-label={
            originOverride
              ? 'Plan from my current location'
              : 'Already planning from your current location'
          }
          sx={{
            position: 'absolute', left: 12, bottom: 12, zIndex: 3,
            width: 42, height: 42, borderRadius: '50%', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(10,16,12,0.85)',
            border: `1px solid ${originOverride ? colors.brand : colors.border}`,
          }}
        >
          <MyLocationIcon
            sx={{ fontSize: 22, color: originOverride ? colors.brand : colors.textMuted }}
          />
        </Box>

        {/* Wave 20.49cv/cw — Map-style cycle + Wind on/off chips,
            top-right of the map. Both sized for a glance. */}
        <Stack direction="row" spacing={1} sx={{ position: 'absolute', top: 12, right: 12, zIndex: 3 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            onClick={cycleMapStyle}
            role="button"
            aria-label={`Change map style — currently ${MAP_STYLE_LABELS[mapStyle]}`}
            sx={{
              px: 1.75, py: 1, borderRadius: 999, cursor: 'pointer',
              backgroundColor: 'rgba(10,16,12,0.85)',
              border: `1px solid ${colors.border}`,
              userSelect: 'none',
            }}
          >
            <LayersIcon sx={{ fontSize: 20, color: colors.brand }} />
            <Typography sx={{ color: colors.text, fontSize: '1rem', fontWeight: 700 }}>
              {MAP_STYLE_LABELS[mapStyle]}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            onClick={toggleWind}
            role="switch"
            aria-checked={windOn}
            aria-label="Toggle wind visualization"
            sx={{
              px: 1.75, py: 1, borderRadius: 999, cursor: 'pointer',
              backgroundColor: 'rgba(10,16,12,0.85)',
              border: `1px solid ${windOn ? colors.brand : colors.border}`,
              userSelect: 'none',
            }}
          >
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: windOn ? colors.brand : colors.textFaint }} />
            <Typography sx={{ color: windOn ? colors.text : colors.textMuted, fontSize: '1rem', fontWeight: 700 }}>
              Wind {windOn ? 'on' : 'off'}
            </Typography>
          </Stack>
        </Stack>
        {/* Wave 20.49cw — Wind chip sized for a glance: 1rem text,
            14/10 px padding (was 0.75rem, 10/4). Rider: "too small". */}
        {windStr && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{
            position: 'absolute', top: 12, left: 12,
            px: 1.75, py: 1, borderRadius: 999,
            backgroundColor: 'rgba(10,16,12,0.85)',
            border: `1px solid ${colors.border}`,
          }}>
            <Typography sx={{ color: colors.text, fontSize: '1rem', fontWeight: 700 }}>
              Wind {windStr}
            </Typography>
          </Stack>
        )}
        {loading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{
            position: 'absolute', top: 68, left: 12,
            px: 1.25, py: 0.75, borderRadius: 1,
            backgroundColor: 'rgba(10,16,12,0.85)',
            border: `1px solid ${colors.border}`,
          }}>
            <CircularProgress size={16} sx={{ color: colors.brand }} />
            <Typography sx={{ color: colors.text, fontSize: '0.85rem' }}>Planning options…</Typography>
          </Stack>
        )}
        {error && (
          <Typography sx={{
            position: 'absolute', top: 68, left: 12,
            px: 1.25, py: 0.75, borderRadius: 1, maxWidth: 320,
            backgroundColor: 'rgba(10,16,12,0.85)',
            border: `1px solid ${colors.border}`,
            color: colors.textMuted, fontSize: '0.85rem',
          }}>{error}</Typography>
        )}
      </Box>

      <Box sx={{ p: 1.5, borderTop: `1px solid ${colors.border}` }}>
        {(data?.options.length ?? 0) > 1 && (
          <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1 }}>
            {(data?.options ?? []).map((opt) => {
              const isSel = opt.id === selectedId;
              return (
                <Stack
                  key={opt.id}
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  onClick={() => setSelectedId(opt.id)}
                  sx={{
                    px: 1.25, py: 0.75, borderRadius: 999,
                    border: `1px solid ${isSel ? colors.brand : colors.border}`,
                    backgroundColor: isSel ? 'rgba(106,255,69,0.10)' : colors.surface,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: colourForIntensity(opt.intensity) }} />
                  <Typography sx={{ color: isSel ? colors.text : colors.textMuted, fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.05em' }}>
                    #{opt.rank}
                  </Typography>
                  <Typography sx={{ color: isSel ? colors.text : colors.textMuted, fontSize: '0.75rem', fontWeight: 700 }}>
                    {fmtDistance(opt.distanceKm * 1000, units)}
                  </Typography>
                  <Typography sx={{ color: colors.textFaint, fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                    {bearingLabel(opt.bearing, opt.direction)}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        )}
        {selected && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ color: colors.brand, fontSize: '0.95rem', fontWeight: 800 }}>{distanceStr}</Typography>
            <Typography sx={{ color: colors.textMuted }}>·</Typography>
            <Typography sx={{ color: colors.textMuted, fontSize: '0.8rem' }}>{durationStr}</Typography>
            {(selected.retracedKm ?? 0) >= 0.5 && (
              <>
                <Typography sx={{ color: colors.textMuted }}>·</Typography>
                {/* Wave 20.49cu — be honest when the terrain forced an
                    out-and-back instead of silently hiding the route. */}
                <Typography sx={{ color: colors.textMuted, fontSize: '0.8rem' }}>
                  {fmtDistance((selected.retracedKm ?? 0) * 1000, units)} out-and-back
                </Typography>
              </>
            )}
            <Typography sx={{ color: colors.textMuted }}>·</Typography>
            <Typography sx={{ color: colors.textMuted, fontSize: '0.8rem' }}>
              Aero intensity{' '}
              <Box component="span" sx={{ color: colourForIntensity(selected.intensity), fontWeight: 800 }}>
                {selected.intensity < 0.34 ? 'low' : selected.intensity < 0.67 ? 'mid' : 'high'}
              </Box>
            </Typography>
          </Stack>
        )}

        {/* Wave 20.50 — Aero brief: where this rider should tuck on this
            loop, and what the wind does at each turn. Collapsed to one
            line by default; the cue list is what they'll hear on the
            ride, so seeing it first builds trust in the voice. */}
        {selected && (briefLoading || brief) && (
          <Box sx={{ mb: 1 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              onClick={() => setBriefOpen((v) => !v)}
              role="button"
              aria-expanded={briefOpen}
              sx={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: colors.data }} />
              <Typography sx={{ color: colors.text, fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.12em' }}>
                AERO BRIEF
              </Typography>
              {briefLoading ? (
                <CircularProgress size={12} sx={{ color: colors.data }} />
              ) : brief && brief.tuckZones.length > 0 ? (
                <Typography sx={{ color: colors.textMuted, fontSize: '0.8rem' }}>
                  {brief.tuckZones.length} aero stretch{brief.tuckZones.length === 1 ? '' : 'es'}
                  {savingsStr ? ` · saves ~${savingsStr}` : ''}
                </Typography>
              ) : (
                <Typography sx={{ color: colors.textMuted, fontSize: '0.8rem' }}>
                  {brief?.windAvailable === false
                    ? 'Wind forecast unavailable — turn cues only'
                    : 'No headwind stretches on this loop'}
                </Typography>
              )}
              <Box sx={{ flex: 1 }} />
              {briefOpen ? (
                <ExpandLessIcon sx={{ fontSize: 18, color: colors.textMuted }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 18, color: colors.textMuted }} />
              )}
            </Stack>

            {briefOpen && brief && (
              <Box sx={{ mt: 1, pl: 2.25 }}>
                {!brief.position.tuckTested && (
                  <Typography sx={{ color: colors.warning, fontSize: '0.72rem', mb: 0.75 }}>
                    Estimated from your {brief.position.derivedFrom ?? 'default'} position — test a
                    tuck in an aero session to make this yours.
                  </Typography>
                )}
                {brief.cues.slice(0, 8).map((c) => (
                  <Typography
                    key={c.id}
                    sx={{
                      color: c.type === 'tuck_start' ? colors.data : colors.textMuted,
                      fontSize: '0.75rem',
                      lineHeight: 1.7,
                    }}
                  >
                    {cueText(c, units, null)}
                  </Typography>
                ))}
                {brief.cues.length > 8 && (
                  <Typography sx={{ color: colors.textFaint, fontSize: '0.72rem', mt: 0.5 }}>
                    +{brief.cues.length - 8} more on the ride
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Wave 20.50 — take it with you. */}
        {selected && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              startIcon={<DirectionsBikeIcon />}
              onClick={() => void onRideThisRoute()}
              disabled={rideState === 'saving'}
              sx={{
                color: colors.textInverse,
                backgroundColor: colors.brand,
                fontWeight: 800,
                px: 1.75,
                '&:hover': { backgroundColor: colors.brandBright },
                '&.Mui-disabled': { backgroundColor: colors.brandDim, color: colors.textInverse },
              }}
            >
              {rideState === 'saving' ? 'Taking…' : 'Take this route'}
            </Button>
            <Button
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={onDownloadGpx}
              sx={{ color: colors.brand, border: `1px solid ${colors.borderFocus}`, px: 1.5 }}
            >
              GPX
            </Button>
            {/* Wave 20.50a — web has no ride recording, so the only
                honest confirmation is telling the rider where it went. */}
            {rideState === 'saved' && (
              <Typography sx={{ color: colors.brand, fontSize: '0.75rem', fontWeight: 700 }}>
                Saved — open Record on your phone and it'll be waiting.
              </Typography>
            )}
            {rideState === 'failed' && (
              <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem' }}>
                Couldn't save it just now — the GPX still works.
              </Typography>
            )}
          </Stack>
        )}

        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ color: colors.textMuted, fontSize: '0.65rem', letterSpacing: '0.15em', fontWeight: 800 }}>
            DISTANCE
          </Typography>
          <Stack direction="row" spacing={0.75} sx={{ overflowX: 'auto', flex: 1 }}>
            {distanceChoices.map((n) => (
              <Box
                key={n}
                onClick={() => setDistanceInUnit(n)}
                sx={{
                  px: 1.25, py: 0.5, borderRadius: 999,
                  border: `1px solid ${distanceInUnit === n ? colors.brand : colors.border}`,
                  backgroundColor: distanceInUnit === n ? 'rgba(106,255,69,0.10)' : 'transparent',
                  color: distanceInUnit === n ? colors.brand : colors.textMuted,
                  fontSize: '0.75rem', fontWeight: 800,
                  cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                }}
              >
                {n} {distanceUnitLabel}
              </Box>
            ))}
          </Stack>
          <IconButton onClick={() => void fetchPlan()} sx={{ color: colors.brand, border: `1px solid ${colors.brand}` }} size="small">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>
    </Dialog>
  );
}
